import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "dropdown", "chips", "output"]

  connect() {
    this.recipients = []
    this.selectedIndex = -1
    this.currentResults = []
    this.dropdownVisible = false
    this.boundClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener("click", this.boundClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.boundClickOutside)
  }

  onInput() {
    const query = this.inputTarget.value.trim()
    if (query.length < 2) {
      this.hideDropdown()
      return
    }
    this.search(query)
  }

  onKeydown(event) {
    if (event.key === "Backspace" && this.inputTarget.value === "" && this.recipients.length > 0) {
      this.removeRecipient(this.recipients.length - 1)
      return
    }

    if (event.key === "Enter" && !this.dropdownVisible) {
      event.preventDefault()
      this.addManualEmail()
      return
    }

    if (!this.dropdownVisible) return

    if (event.key === "Escape") {
      event.preventDefault()
      this.hideDropdown()
    } else if (event.key === "ArrowDown") {
      event.preventDefault()
      this.moveSelection(1)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.moveSelection(-1)
    } else if (event.key === "Enter" && this.selectedIndex >= 0) {
      event.preventDefault()
      this.selectCurrent()
    }
  }

  onBlur() {
    setTimeout(() => this.addManualEmail(), 200)
  }

  async search(query) {
    const response = await fetch(`/search/cc_recipients?q=${encodeURIComponent(query)}`)
    const results = await response.json()

    if (results.length === 0) {
      this.hideDropdown()
      return
    }

    this.currentResults = results.filter(r => !this.recipients.some(rec => rec.email === r.email))
    if (this.currentResults.length === 0) {
      this.hideDropdown()
      return
    }

    this.selectedIndex = 0
    this.renderDropdown()
  }

  renderDropdown() {
    const dropdown = this.dropdownTarget
    dropdown.innerHTML = this.currentResults.map((result, index) => {
      const selected = index === this.selectedIndex ? "bg-gold/10" : ""
      const icon = result.type === "user"
        ? '<div class="w-6 h-6 rounded-full bg-gold/20 flex items-center justify-center text-[10px] font-bold text-gold">' + this.initials(result.label) + '</div>'
        : '<div class="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center text-[10px] font-bold text-blue-400">' + this.initials(result.label) + '</div>'
      return `<button type="button"
        class="w-full text-left px-3 py-2 hover:bg-gold/10 transition-colors flex items-center justify-between gap-2 ${selected}"
        data-action="click->cc-recipients#selectResult"
        data-index="${index}">
        <div class="flex items-center gap-2">
          ${icon}
          <div class="min-w-0">
            <span class="text-sm font-medium text-text-primary">${this.escape(result.label)}</span>
            <span class="text-xs text-text-muted ml-1">${this.escape(result.email)}</span>
          </div>
        </div>
        <span class="text-[10px] text-text-muted flex-shrink-0">${this.escape(result.subtitle || "")}</span>
      </button>`
    }).join("")

    dropdown.classList.remove("hidden")
    this.dropdownVisible = true
  }

  selectResult(event) {
    event.preventDefault()
    event.stopPropagation()
    const index = parseInt(event.currentTarget.dataset.index)
    const result = this.currentResults[index]
    if (result) this.addRecipient(result.email, result.label)
  }

  selectCurrent() {
    if (this.selectedIndex < 0 || !this.currentResults[this.selectedIndex]) return
    const result = this.currentResults[this.selectedIndex]
    this.addRecipient(result.email, result.label)
  }

  addManualEmail() {
    const value = this.inputTarget.value.trim()
    if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return
    this.addRecipient(value, value)
  }

  addRecipient(email, label) {
    if (this.recipients.some(r => r.email === email)) return

    this.recipients.push({ email, label })
    this.inputTarget.value = ""
    this.hideDropdown()
    this.renderChips()
    this.syncOutput()
    this.inputTarget.focus()
  }

  removeRecipient(index) {
    this.recipients.splice(index, 1)
    this.renderChips()
    this.syncOutput()
  }

  removeChip(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    this.removeRecipient(index)
  }

  renderChips() {
    this.chipsTarget.innerHTML = this.recipients.map((rec, index) =>
      `<span class="inline-flex items-center gap-1 px-2 py-0.5 bg-gold/10 text-gold text-xs rounded-full">
        ${this.escape(rec.label)}
        <button type="button" data-action="cc-recipients#removeChip" data-index="${index}" class="hover:text-danger transition-colors cursor-pointer">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </span>`
    ).join("")
  }

  syncOutput() {
    this.outputTarget.value = this.recipients.map(r => r.email).join(", ")
  }

  moveSelection(direction) {
    this.selectedIndex = Math.max(0, Math.min(this.currentResults.length - 1, this.selectedIndex + direction))
    this.renderDropdown()
  }

  hideDropdown() {
    if (this.hasDropdownTarget) this.dropdownTarget.classList.add("hidden")
    this.dropdownVisible = false
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) this.hideDropdown()
  }

  initials(name) {
    return this.escape((name || "").split(" ").map(n => n[0]).join("").substring(0, 2))
  }

  escape(text) {
    const div = document.createElement("div")
    div.textContent = text || ""
    return div.innerHTML
  }
}
